import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import { motion } from 'framer-motion';
import * as Icons from '@mui/icons-material';
import { gradientPrimarySecondary } from '../theme/brandStyles';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";

// Import du système de tracking de conversion (chemin corrigé)
import ConversionTracker from '../components/ConversionTracker';

const MetierB2B = ({ metier }) => {
  const navigate = useNavigate();

  // Toujours appeler les hooks en haut du composant
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-XXXXXXXXXX', {
        page_path: window.location.pathname,
        page_title: `${metier?.nom || ''} B2B - LYS Platform`
      });
    }
  }, [metier]);

  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName] || Icons.Build;
    return IconComponent;
  };

  const HeroIcon = getIcon(metier.icon);

  // Configuration carrousels
  const beneficesSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3500,
    arrows: false
  };

  const temoignagesSettings = {
    dots: true,
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: false
  };

  // Données B2B spécifiques
  const roiData = metier.b2b?.roi || {
    timeToValue: "48h",
    clientsGain: "+127",
    caSupplement: "+35%",
    tempsGagne: "12h/sem"
  };

  const pricingPlans = metier.b2b?.pricing || [
    {
      nom: "Starter",
      prix: "49€",
      periode: "/mois",
      features: [
        "1 utilisateur",
        "Clients illimités",
        "Dashboard complet",
        "Support email"
      ],
      cta: "Démarrer"
    },
    {
      nom: "Pro",
      prix: "99€",
      periode: "/mois",
      features: [
        "5 utilisateurs",
        "Clients illimités",
        "Analytics avancés",
        "Support prioritaire",
        "API access"
      ],
      cta: "Choisir Pro",
      recommended: true
    },
    {
      nom: "Enterprise",
      prix: "Sur devis",
      periode: "",
      features: [
        "Utilisateurs illimités",
        "Multi-sites",
        "Support dédié 24/7",
        "Intégrations custom",
        "Formation équipe"
      ],
      cta: "Nous contacter"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Conversion Tracker */}
      <ConversionTracker source={`b2b-${metier.id}`} />

      {/* Hero B2B - Même layout que pages métier */}
      <Box
        sx={{
          background: gradientPrimarySecondary,
          color: 'white',
          py: { xs: 3, sm: 5, md: 8 },
          px: { xs: 2, sm: 3, md: 0 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
            {/* Texte fullwidth sans icône */}
            <Grid item xs={12}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Typography 
                  variant="h1" 
                  fontWeight="bold" 
                  gutterBottom 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' },
                    lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
                    mb: { xs: 2, sm: 3, md: 3 }
                  }}
                >
                  {metier.b2b.titre}
                </Typography>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    mb: { xs: 2, sm: 3, md: 4 }, 
                    opacity: 0.9, 
                    fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.5rem' },
                    lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
                    fontWeight: 400
                  }}
                >
                  {metier.b2b.soustitre}
                </Typography>
                <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2, md: 2.5 }, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate(`/inscription-${getInscriptionSlug(metier.id)}`)}
                    sx={{
                      backgroundColor: 'white',
                      color: 'primary.main',
                      fontWeight: 'bold',
                      px: { xs: 2, sm: 3, md: 4 },
                      py: { xs: 1, sm: 1.2, md: 1.5 },
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': {
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                  >
                    Essai gratuit 14 jours
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/contact-demo')}
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      fontWeight: 'bold',
                      px: { xs: 2, sm: 3, md: 4 },
                      py: { xs: 1, sm: 1.2, md: 1.5 },
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: (theme) => theme.palette.action.hover
                      }
                    }}
                  >
                    Voir une démo
                  </Button>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Avantages B2B */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5, md: 8 }, px: { xs: 2, sm: 3, md: 3 } }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <Typography 
            variant="h3" 
            textAlign="center" 
            fontWeight="bold" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '1.4rem', sm: '1.9rem', md: '2.5rem' },
              lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 },
              px: { xs: 2, sm: 0 },
              mb: { xs: 1.5, sm: 2, md: 3 }
            }}
          >
            Pourquoi choisir LYS pour votre entreprise ?
          </Typography>
          <Typography 
            variant="h6" 
            textAlign="center" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 3, sm: 4, md: 6 }, 
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.15rem' },
              lineHeight: { xs: 1.5, sm: 1.6 },
              px: { xs: 2, sm: 0 },
              fontWeight: 400
            }}
          >
            Des fonctionnalités pensées pour les professionnels exigeants
          </Typography>
        </motion.div>

        <Slider {...beneficesSettings}>
          {metier.benefices.slice(0, 4).map((benefice, index) => {
            const BeneficeIcon = getIcon(benefice.icon);
            return (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <Box sx={{ px: 2 }}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 3,
                      border: (theme) => `2px solid ${theme.palette.primary.main}`,
                      '&:hover': {
                        boxShadow: 8,
                        transform: 'translateY(-5px)',
                        transition: 'all 0.3s'
                      }
                    }}
                  >
                    <CardContent>
                      <BeneficeIcon sx={{ fontSize: { xs: 35, sm: 40, md: 50 }, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h5" fontWeight="bold" gutterBottom color="primary.main">
                        {benefice.titre}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {benefice.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </motion.div>
            );
          })}
        </Slider>
      </Container>

      {/* Pricing Plans */}
      <Box sx={{ backgroundColor: (theme) => theme.palette.action.hover, py: { xs: 4, sm: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Typography variant="h3" textAlign="center" fontWeight="bold" gutterBottom>
              Tarifs transparents et adaptés
            </Typography>
            <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
              Choisissez le plan qui correspond à votre activité
            </Typography>
          </motion.div>

          <Grid container spacing={4} justifyContent="center">
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.15 }}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 4,
                      borderRadius: 3,
                      position: 'relative',
                      border: plan.recommended
                        ? (theme) => `3px solid ${theme.palette.primary.main}`
                        : '1px solid #e0e0e0',
                      boxShadow: plan.recommended ? 8 : 2
                    }}
                  >
                    {plan.recommended && (
                      <Chip
                        label="RECOMMANDÉ"
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                    <CardContent>
                      <Typography variant="h5" fontWeight="bold" gutterBottom color="primary.main">
                        {plan.nom}
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h3" fontWeight="bold" component="span">
                          {plan.prix}
                        </Typography>
                        <Typography variant="h6" component="span" color="text.secondary">
                          {plan.periode}
                        </Typography>
                      </Box>
                      <List>
                        {plan.features.map((feature, idx) => (
                          <ListItem key={idx} disablePadding sx={{ mb: 1 }}>
                            <ListItemIcon>
                              <Icons.CheckCircle color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={feature} />
                          </ListItem>
                        ))}
                      </List>
                      <Button
                        variant={plan.recommended ? "contained" : "outlined"}
                        fullWidth
                        size="large"
                        onClick={() => navigate(`/inscription-${getInscriptionSlug(metier.id)}`)}
                        sx={{
                          mt: 3,
                          fontWeight: 'bold',
                          py: 1.5
                        }}
                      >
                        {plan.cta}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Case Studies / Témoignages */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <Typography variant="h3" textAlign="center" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}>
            Ils nous font confiance
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: { xs: 3, sm: 4, md: 6 }, fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}>
            Découvrez comment nos clients ont transformé leur activité
          </Typography>
        </motion.div>

        <Slider {...temoignagesSettings}>
          {metier.temoignages.map((temoignage, index) => (
            <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
              <Box sx={{ px: 2 }}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    borderRadius: 3,
                    boxShadow: 4
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 60,
                          height: 60,
                          bgcolor: 'primary.main',
                          fontSize: '1.5rem',
                          mr: 2
                        }}
                      >
                        {temoignage.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {temoignage.nom}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {temoignage.entreprise}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 2 }}>
                      "{temoignage.texte}"
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: (theme) => theme.palette.action.hover,
                        p: 2,
                        borderRadius: 2
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold" color="primary.main">
                        {temoignage.chiffre}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {temoignage.metier}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </motion.div>
          ))}
        </Slider>
      </Container>

      {/* CTA Final B2B */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
          color: 'white',
          py: 10
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Prêt à transformer votre entreprise ?
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
              Rejoignez {metier.stats.utilisateurs} {metier.nomPluriel} qui ont déjà fait le choix LYS
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(`/inscription-${getInscriptionSlug(metier.id)}`)}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  fontWeight: 'bold',
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                Commencer gratuitement
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/contact-demo')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  fontWeight: 'bold',
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  borderWidth: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    borderWidth: 2,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Parler à un expert
              </Button>
            </Box>
            <Typography variant="body2" sx={{ mt: 3, opacity: 0.8 }}>
              ✓ Essai gratuit 14 jours • ✓ Sans engagement • ✓ Support dédié
            </Typography>
          </motion.div>
        </Container>
      </Box>
    </motion.div>
  );
};

export default MetierB2B;
